'use client';

import {registerStoreForEnvVariablesAccess} from '@web-catalog/env';
import {createStore, Provider as JotaiProvider} from 'jotai';
import {FC, PropsWithChildren, useMemo, useRef} from 'react';

import {OmicronContent} from '@web-catalog/api';

import {AppCategory} from '@web-catalog/types/category';
import {EnvConfig} from '@web-catalog/types/env';

import {HydrateAtoms} from '@web-catalog/store';

import {MultiProviderFloorAd} from '../Ads/MultiProviderFloorAd';
import {OmicronClientAB} from '../OmicronClient';
import {PageViewReporter} from '../PageViewReporter/PageViewReporter';
import HeadlessUiDocumentPaddingObserver from './HeadlessUiDocumentPaddingObserver';

type Props = PropsWithChildren<{
  envConfig: EnvConfig;
  categories?: {
    content: Array<AppCategory>;
    contentGames: Array<AppCategory>;
  };
  omicronConfig: OmicronContent;
}>;

const useInitProvider = () => {
  const {current: store} = useRef(createStore());
  registerStoreForEnvVariablesAccess(store);

  return store;
};

export const Providers: FC<Props> = ({
  children,
  envConfig,
  categories,
  omicronConfig,
}) => {
  const store = useInitProvider();

  const categoriesInit = useMemo(
    () => ({
      appsCategories: categories?.content ?? [],
      gamesCategories: categories?.contentGames ?? [],
    }),
    [categories],
  );

  return (
    <JotaiProvider store={store}>
      <HydrateAtoms
        envConfig={envConfig}
        categories={categoriesInit}
        omicronConfig={omicronConfig}
      >
        <HeadlessUiDocumentPaddingObserver />

        <OmicronClientAB />

        {children}

        <MultiProviderFloorAd />

        <PageViewReporter />
      </HydrateAtoms>
    </JotaiProvider>
  );
};

export const LightLayoutProvider: FC<Props> = ({
  children,
  envConfig,
  categories,
  omicronConfig,
}) => {
  const store = useInitProvider();

  const categoriesInit = useMemo(
    () => ({
      appsCategories: categories?.content ?? [],
      gamesCategories: categories?.contentGames ?? [],
    }),
    [categories],
  );

  return (
    <JotaiProvider store={store}>
      <HydrateAtoms
        envConfig={envConfig}
        categories={categoriesInit}
        omicronConfig={omicronConfig}
      >
        {children}
      </HydrateAtoms>
    </JotaiProvider>
  );
};
